import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, effect, inject, viewChild } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { extractHttpErrorMessage } from '../../utils/http-error.util';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { AgenticAiApiService } from '../../../features/agentic-ai/agentic-ai-api.service';
import { AgentChatStateService } from '../../../features/agentic-ai/agent-chat-state.service';
import type { AgenticAiConversation } from '../../../features/agentic-ai/agentic-ai.models';
import { conversationHasSkill } from '../../../features/skills/skill-slash.util';
import { ToastService } from '../../services/toast.service';
import { NetworkConnectivityService } from '../../services/network-connectivity.service';

@Component({
  selector: 'app-recent-chats',
  imports: [CommonModule, RouterLink, RouterLinkActive, ConfirmDialogModule, TooltipModule],
  templateUrl: './recent-chats.html',
  styleUrl: './recent-chats.css',
  providers: [ConfirmationService],
})
export class RecentChats {
  readonly state = inject(AgentChatStateService);
  readonly network = inject(NetworkConnectivityService);
  private readonly scrollHost = viewChild<ElementRef<HTMLElement>>('scrollHost');
  private readonly api = inject(AgenticAiApiService);
  /** Skip auto-scroll when only older pages are appended (Load more); scroll when the top item changes. */
  private prevFirstConversationId: string | undefined = undefined;

  isTitleClipped(el: HTMLElement | null | undefined): boolean {
    if (!el) {
      return false;
    }
    return el.scrollWidth > el.clientWidth;
  }

  /** Sidebar label: first character uppercase (rest unchanged). */
  displayChatTitle(title?: string | null): string {
    const t = title?.trim() || 'Untitled chat';
    if (!t) {
      return 'Untitled chat';
    }
    return t.charAt(0).toUpperCase() + t.slice(1);
  }

  constructor() {
    effect(() => {
      const items = this.state.conversations();
      if (items.length === 0) {
        this.prevFirstConversationId = undefined;
        return;
      }
      const firstId = items[0]?.id;
      const topChanged =
        firstId !== undefined && firstId !== this.prevFirstConversationId;
      if (!topChanged && this.prevFirstConversationId !== undefined) {
        return;
      }
      this.prevFirstConversationId = firstId;
      queueMicrotask(() => {
        const el = this.scrollHost()?.nativeElement;
        if (el) {
          el.scrollTop = 0;
        }
      });
    });
  }

  private readonly router = inject(Router);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly toast = inject(ToastService);
  activeMenuId: string | null = null;
  activeMenuConversation: AgenticAiConversation | null = null;
  menuStyle: { top: number; left: number } | null = null;
  actionError: string | null = null;
  /** Bound to delete confirm dialog message template. */
  deleteConfirmChatTitle = '';

  readonly recentChatDeleteConfirmKey = 'recentChatDelete';

  toggleMenu(event: MouseEvent, c: AgenticAiConversation): void {
    event.preventDefault();
    event.stopPropagation();
    this.actionError = null;
    if (this.activeMenuId === c.id) {
      this.closeMenu();
      return;
    }
    const btn = event.currentTarget as HTMLElement;
    const rect = btn.getBoundingClientRect();
    const menuWidth = 140;
    this.activeMenuId = c.id;
    this.activeMenuConversation = c;
    this.menuStyle = {
      top: Math.round(rect.bottom + 4),
      left: Math.round(Math.max(8, rect.right - menuWidth)),
    };
  }

  private closeMenu(): void {
    this.activeMenuId = null;
    this.activeMenuConversation = null;
    this.menuStyle = null;
  }

  isPinned(c: AgenticAiConversation): boolean {
    return Number(c.pinned ?? 0) === 1;
  }

  hasSkill(c: AgenticAiConversation): boolean {
    return conversationHasSkill(c.metadata);
  }

  togglePin(event: MouseEvent, c: AgenticAiConversation): void {
    event.preventDefault();
    event.stopPropagation();
    this.actionError = null;
    this.api.pinConversation(c.id, !this.isPinned(c)).subscribe({
      next: () => {
        this.closeMenu();
        this.state.refreshConversations();
      },
      error: (err: unknown) => {
        this.actionError = this.formatError(err, 'Failed to update pin.');
      },
    });
  }

  deleteConversation(event: MouseEvent, c: AgenticAiConversation): void {
    event.preventDefault();
    event.stopPropagation();
    this.actionError = null;
    this.deleteConfirmChatTitle = this.displayChatTitle(c.title);
    this.confirmationService.confirm({
      key: this.recentChatDeleteConfirmKey,
      header: 'Delete Chat',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      rejectButtonStyleClass: 'p-button-secondary',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.api.deleteConversation(c.id).subscribe({
          next: (res) => {
            this.closeMenu();
            this.state.removeConversationFromList(c.id);
            if (this.state.activeConversationId() === c.id) {
              void this.router.navigate(['/ai-agent'], { replaceUrl: true });
            }
            this.state.refreshConversations();
            this.toast.success(res?.message || 'Chat deleted successfully.');
          },
          error: (err: unknown) => {
            const msg = this.formatError(err, 'Failed to delete chat.');
            this.actionError = msg;
            this.toast.error(msg);
          },
        });
      },
    });
  }

  @HostListener('document:click')
  closeMenuOnOutsideClick(): void {
    this.closeMenu();
  }

  closeMenuOnScroll(): void {
    this.closeMenu();
  }

  loadMore(): void {
    this.state.loadMoreConversations();
  }

  private formatError(err: unknown, fallback: string): string {
    return extractHttpErrorMessage(err, fallback);
  }
}
